import dayjs from 'dayjs';
import { MapUtils } from '../../utility/map-utils.js';
import { Country } from './country.js';
import { TimeZoneRegion } from './time-zone-region.js';
import { DataModel, DataModelBuilder, isSameData, mapToDataOrNull, SerializationContext, SerializedData } from '../dto/dto.js';
import { ComponentList } from '../../components/core/component-list.js';
import { getLanguageOfCountry, Language } from './language.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { BusinessModuleType } from './business-module-type.js';
import { Discoverable } from '../discoverable.js';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../serialization/enum-utils.js';
import { SeatingSettingsComponent } from '../../components/events/seating-settings-component.js';
import { LegacySeatingSettingsComponent } from '../../components/events/legacy-seating-settings-component.js';
import { isNotNullOrUndefined } from '../../utility/null-safety.js';
import { VersionRange } from '../core/version-range.js';
import { LegacySerialNumberStrategy } from './serial-numbers/legacy-serial-number-strategy.js';
import { SerialNumberScope } from './serial-numbers/serial-number-scope.js';
import { DynamicPricingSettingsComponent } from '../../components/events/dynamic-pricing-settings-component.js';
import { DateSpanComponent } from '../../components/misc/date-span-component.js';
import { TimeZone } from '../../time/timezone.js';
import { EventProfileAssociations } from './event-profile-associations.js';
import { SerialNumberStrategy, SerialNumberStrategyUtils } from './serial-numbers/serial-number-strategy.js';
import { QRCodeSeparator } from '../../utility/qr-code-utils.js';
import { TicketPoolStrategy } from './ticketing-state/ticket-pool-strategy.js';
import { BackstageEventComponent } from '../../components/events/backstage-event-component.js';
import { LocalizedNameComponent } from '../../components/point-of-interest/localized-name-component.js';
import { TicketTemplate } from './ticket-template';
import { EventBookingProtocol } from './event-booking-protocol/event-booking-protocol.js';
import { TicketTemplateComponent } from '../../components/events/ticket-template-component.js';
import { LegacyTicketBookingSettingsComponent } from '../../components/events/legacy-ticket-booking-settings-component.js';
import { TicketsOnlyEventBookingProtocol } from './event-booking-protocol/tickets-only-event-booking-protocol.js';
import { EventBookingStrategy } from './event-booking-protocol/event-booking-strategy.js';
import { ProductListComponent } from '../../components/events/product-list-component.js';
import { VouchersOnlyEventBookingProtocol } from './event-booking-protocol/vouchers-only-event-booking-protocol.js';
import { EventPrivacySettingsComponent } from '../../components/events/event-privacy-settings-component.js';
import { LegacyPaymentSettingsComponent } from '../../components/payment/legacy-payment-settings-component.js';
import { PaymentProtocolComponent } from '../../components/payment/payment-protocol-component.js';
import { PaymentProtocolSettings } from './payment-protocol-settings.js';
import { PaymentProtocol } from './payment-protocol.js';
import { LocationComponent } from '../../components/events/location-component.js';
import { PlaceComponent } from '../../components/events/place-component.js';
import { Place } from './place.js';
import { TicketAsPassEventBookingProtocol } from './event-booking-protocol/ticket-as-pass-event-booking-protocol.js';
import { OpenAccessPassComponent } from '../../components/voucher/open-access-pass-component.js';

export enum EventProfileStatus {
  Live = 'LIVE',
  Archived = 'ARCHIVED',
  Closed = 'CLOSED',
  Canceled = 'CANCELED',
}

export class EventProfile extends Discoverable {
  static readonly TYPE_ID: string = '61b732b763e04974a096748c9c1514c6';
  static readonly SCHEMA_VERSION: number = 11;

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly country: Country,
    public readonly language: Language,
    public readonly timezone: TimeZoneRegion,
    public readonly businessRef: BusinessModuleRef,
    public readonly webRoute: string | null,
    public readonly components: ComponentList,

    /**
     * @deprecated Use LocationComponent instead.
     */
    public readonly placeId: string | null,

    public readonly productionId: string | null,
    public readonly status: EventProfileStatus,
    public shortCode: string | null,
    public readonly serialNumberScope: SerialNumberScope,
    public readonly serialNumberStrategy: SerialNumberStrategy,
    public readonly ticketPoolStrategy: TicketPoolStrategy,
    public readonly qrCodeSeparator: QRCodeSeparator,
    public readonly associations: EventProfileAssociations,
    public readonly bookingProtocol: EventBookingProtocol,
  ) {
    super();
  }

  get organizationId(): string {
    return this.businessRef.organizationId ?? this.creatorId;
  }

  get moduleType(): BusinessModuleType {
    return this.businessRef.moduleType ?? BusinessModuleType.Legacy;
  }

  get bookingStrategy(): EventBookingStrategy {
    return this.bookingProtocol.strategy;
  }

  get $typeId() {
    return EventProfile.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(10, EventProfile.SCHEMA_VERSION);
  }

  static create(opts: {
      uniqueId: string,
      creatorId: string,
      creationDate: dayjs.Dayjs,
      country: Country,
      language: Language,
      timezone: TimeZoneRegion,
      businessRef: BusinessModuleRef,
      webRoute: string,
      components: ComponentList,

      /**
       * @deprecated Use LocationComponent instead.
       */
      placeId: string,

      productionId: string | null,
      shortCode: string | null,
      serialNumberScope: SerialNumberScope,
      serialNumberStrategy: SerialNumberStrategy,
      ticketPoolStrategy: TicketPoolStrategy,
      bookingProtocol: EventBookingProtocol,
      qrCodeSeparator?: QRCodeSeparator,
      associations?: EventProfileAssociations,
  }): EventProfile {
    return new EventProfile(
        opts.uniqueId,
        opts.creatorId,
        opts.creationDate,
        opts.country,
        opts.language,
        opts.timezone,
        opts.businessRef,
        opts.webRoute,
        opts.components,
        opts.placeId,
        opts.productionId,
        EventProfileStatus.Live,
        opts.shortCode,
        opts.serialNumberScope,
        opts.serialNumberStrategy,
        opts.ticketPoolStrategy,
        opts.qrCodeSeparator ?? QRCodeSeparator.Dot,
        opts.associations ?? EventProfileAssociations.create({ artists: [], brands: [], organizers: [] }),
        opts.bookingProtocol,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): EventProfile {
    return new EventProfile(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'creatorId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'country'), Country),
        toEnumFromString(MapUtils.get<string>(map, 'language'), Language),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.getOrNull<string>(map, 'webRoute'),
        ComponentList.deserialize(
            context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
        ),
        MapUtils.getOrNull<string>(map, 'placeId'),
        MapUtils.getOrNull<string>(map, 'productionId'),
        toEnumFromString(MapUtils.get<string>(map, '$status'), EventProfileStatus),
        MapUtils.getOrNull<string>(map, 'shortCode'),
        toEnumFromString(MapUtils.get<string>(map, 'serialNumberScope'), SerialNumberScope),
        toEnumFromString(MapUtils.getString(map, 'serialNumberStrategy'), SerialNumberStrategy),
        toEnumFromString(MapUtils.get<string>(map, 'ticketPoolStrategy'), TicketPoolStrategy),
        toEnumFromString(MapUtils.get<string>(map, 'qrCodeSeparator'), QRCodeSeparator),
        context.fromData(MapUtils.get<SerializedData>(map, 'associations')),
        context.fromData(MapUtils.get<SerializedData>(map, 'bookingProtocol')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      '$status': fromEnumAsString(this.status),
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'creationDate': context.encodeDate(this.creationDate),
      'country': fromEnumAsString(this.country),
      'language': fromEnumAsString(this.language),
      'timezone': fromEnumAsString(this.timezone),
      'businessRef': this.businessRef.encode(),
      'webRoute': this.webRoute,
      'components': this.components.serialize(context),
      'placeId': this.placeId,
      'productionId': this.productionId,
      'shortCode': this.shortCode,
      'serialNumberScope': fromEnumAsString(this.serialNumberScope),
      'serialNumberStrategy': fromEnumAsString(this.serialNumberStrategy),
      'ticketPoolStrategy': fromEnumAsString(this.ticketPoolStrategy),
      'qrCodeSeparator': fromEnumAsString(this.qrCodeSeparator),
      'associations': context.toData(this.associations),
      'bookingProtocol': context.toData(this.bookingProtocol),
      'bookingStrategy': fromEnumAsString(this.bookingStrategy),
    };
  }

  clone(): this {
    return new EventProfile(
        this.uniqueId,
        this.creatorId,
        this.creationDate,
        this.country,
        this.language,
        this.timezone,
        this.businessRef.clone(),
        this.webRoute,
        this.components.clone(),
        this.placeId,
        this.productionId,
        this.status,
        this.shortCode,
        this.serialNumberScope,
        this.serialNumberStrategy,
        this.ticketPoolStrategy,
        this.qrCodeSeparator,
        this.associations.clone(),
        this.bookingProtocol.clone(),
    ) as this;
  }

  isSame(other: EventProfile): boolean {
    if(other === this) {
      return true;
    }

    return other.status === this.status &&
      other.uniqueId === this.uniqueId &&
      other.creatorId === this.creatorId &&
      other.country === this.country &&
      other.creationDate === this.creationDate &&
      other.timezone === this.timezone &&
      other.language === this.language &&
      isSameData(other.businessRef, this.businessRef) &&
      other.webRoute === this.webRoute &&
      other.serialNumberScope === this.serialNumberScope &&
      other.serialNumberStrategy === this.serialNumberStrategy &&
      other.ticketPoolStrategy === this.ticketPoolStrategy &&
      other.qrCodeSeparator === this.qrCodeSeparator &&
      other.components.isSame(this.components) &&
      other.shortCode === this.shortCode &&
      other.placeId === this.placeId &&
      other.productionId === this.productionId &&
      isSameData(other.associations, this.associations) &&
      isSameData(other.bookingProtocol, this.bookingProtocol);
  }

  copyWith(opts?: {
    qrCodeSeparator?: QRCodeSeparator,
    ticketPoolStrategy?: TicketPoolStrategy,
    serialNumberScope?: SerialNumberScope,
    serialNumberStrategy?: SerialNumberStrategy,

    /**
     * @deprecated Use LocationComponent instead.
     */
    placeId?: string,

    bookingProtocol?: EventBookingProtocol,
    status?: EventProfileStatus,
  }): this {
    return new EventProfile(
        this.uniqueId,
        this.creatorId,
        this.creationDate,
        this.country,
        this.language,
        this.timezone,
        this.businessRef.clone(),
        this.webRoute,
        this.components.clone(),
        opts?.placeId ?? this.placeId,
        this.productionId,
        opts?.status ?? this.status,
        this.shortCode,
        opts?.serialNumberScope ?? this.serialNumberScope,
        opts?.serialNumberStrategy ?? this.serialNumberStrategy,
        opts?.ticketPoolStrategy ?? this.ticketPoolStrategy,
        opts?.qrCodeSeparator ?? this.qrCodeSeparator,
        this.associations.clone(),
        opts?.bookingProtocol ?? this.bookingProtocol.clone(),
    ) as this;
  }

  static encodedStatusSafe(status: EventProfileStatus): string {
    return fromEnumAsString(status);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof EventProfile;
  }

  static assertType(order: DataModel): order is EventProfile {
    return order instanceof EventProfile;
  }

  hasSeating(): boolean {
    return this.components.any((c) => {
      if(SeatingSettingsComponent.assertType(c)) {
        return isNotNullOrUndefined(c.seatingLayoutId);
      }
      else if(LegacySeatingSettingsComponent.assertType(c)) {
        return isNotNullOrUndefined(c.seatingLayoutId);
      }

      return false;
    });
  }

  findTicketById(ticketId: string): TicketTemplate | null {
    const component = this.findComponentByTypeId<TicketTemplateComponent>(TicketTemplateComponent.TYPE_ID);
    if(component != null) {
      for(const ticket of component.tickets) {
        if(ticket.uniqueId === ticketId) {
          return ticket;
        }
      }
    }

    return null;
  }
}

export class EventProfileBuilder extends DataModelBuilder<EventProfile> {
  build(context: SerializationContext, map: SerializedData): EventProfile {
    return EventProfile.fromMap(context, map);
  }

  optimizeForQuery(context: SerializationContext, model: EventProfile): SerializedData | null {
    const dateSpanComponent = model.findComponentByTypeId<DateSpanComponent>(DateSpanComponent.TYPE_ID);
    const localizedNameComponent = model.findComponentByTypeId<LocalizedNameComponent>(LocalizedNameComponent.TYPE_ID);
    const eventPrivacySettingsComponent = model.findComponentByTypeId<EventPrivacySettingsComponent>(EventPrivacySettingsComponent.TYPE_ID);
    const locationComponent = model.findComponentByTypeId<LocationComponent>(LocationComponent.TYPE_ID);
    return {
      'hasBackstage': model.hasComponentByTypeId(BackstageEventComponent.TYPE_ID),
      'hasDynamicPricing': model.hasComponentByTypeId(DynamicPricingSettingsComponent.TYPE_ID),
      'startDate': TimeZone.encodeDateOrNull(dateSpanComponent?.dateSpan?.from ?? null),
      'endDate': TimeZone.encodeDateOrNull(dateSpanComponent?.dateSpan?.to ?? null),
      'locationId': locationComponent?.uniqueId ?? model.placeId,
      'names': mapToDataOrNull(localizedNameComponent?.names?.localizations, (k, v) => {
        return { key: fromEnumAsString(k), value: v };
      }),
      'privacyMode': fromEnumAsStringOrNull(eventPrivacySettingsComponent?.privacyMode ?? null),
    };
  }

  onBeforeDeserialization(context: SerializationContext, map: SerializedData): void {
    const language = toEnumFromStringOrNull(MapUtils.get<string>(map, 'language'), Language) ??
        getLanguageOfCountry(toEnumFromString(MapUtils.get<string>(map, 'country'), Country));

    context.storage.set('language', language);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, 'qrCodeSeparator', QRCodeSeparator, QRCodeSeparator.Dot);

    if(versionRange.contains(EventProfile.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(EventProfile.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version: number = versionRange.to;

    if(version < 2) {
      map['serialNumberStrategy'] = fromEnumAsString(LegacySerialNumberStrategy.AssignByTicketType);
      map['serialNumberScope'] = fromEnumAsString(SerialNumberScope.Event);
      map['shortCode'] = null;
      version = 2;
    }

    /**
     * Version 3:
     * - add `associations` of type {@link EventProfileAssociations}.
     */
    if(version < 3) {
      map['associations'] ??= context.toData(EventProfileAssociations.empty());
      version = 3;
    }

    /**
     * Version 4:
     * - change `serialNumberStrategy` from {@link LegacySerialNumberStrategy} to {@link SerialNumberStrategy}.
     */
    if(version < 4) {
      const legacyStrategy = toEnumFromString(MapUtils.get<string>(map, 'serialNumberStrategy'), LegacySerialNumberStrategy) as LegacySerialNumberStrategy;
      const strategy = SerialNumberStrategyUtils.fromLegacy(legacyStrategy);

      MapUtils.set(map, 'serialNumberStrategy', fromEnumAsString(strategy));
      version = 4;
    }

    /**
     * Version 5:
     * - add `qrCodeSeparator` of type {@link QRCodeSeparator}.
     */
    if(version < 5) {
      MapUtils.set(map, 'qrCodeSeparator', fromEnumAsString(QRCodeSeparator.Dot));
      version = 5;
    }

    /**
     * Version 6:
     * - add `ticketPoolStrategy` of type {@link TicketPoolStrategy}.
     */
    if(version < 6) {
      MapUtils.setIfNullOrUndefined(map, 'ticketPoolStrategy', fromEnumAsString(TicketPoolStrategy.Legacy));
      version = 6;
    }

    /**
     * Version 7:
     * - add `productionId` of type `string | null`.
     */
    if(version < 7) version = 7;

    /**
     * Version 8:
     * - add `bookingProtocol` of type {@link EventBookingProtocol}.
     */
    if(version < 8) {
      const components = ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
      );

      const settingsComponent = components.findComponentByTypeId<LegacyTicketBookingSettingsComponent>(LegacyTicketBookingSettingsComponent.TYPE_ID);
      const productListComponent = components.findComponentByTypeId<ProductListComponent>(ProductListComponent.TYPE_ID);
      const hasProductList = productListComponent != null;

      const bookingProtocol: EventBookingProtocol = settingsComponent?.settings?.toEventBookingProtocol({ hasProductList }) ??
        (hasProductList ? VouchersOnlyEventBookingProtocol.create() : TicketsOnlyEventBookingProtocol.create());

      map['bookingProtocol'] = context.toData(bookingProtocol);
      map['components'] = components.serialize(context);

      version = 8;
    }

    /**
     * Version 9:
     * - replace {@link LegacyPaymentSettingsComponent} with {@link PaymentProtocolComponent}.
     */
    if(version < 9) {
      const components = ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
      );

      const paymentSettingsComponent = components.findComponentByTypeId<LegacyPaymentSettingsComponent>(LegacyPaymentSettingsComponent.TYPE_ID);
      if(paymentSettingsComponent != null && paymentSettingsComponent.settings.paymentAccount != null) {
        const paymentProtocolComponent = new PaymentProtocolComponent(
            PaymentProtocolSettings.create({
              standard: PaymentProtocol.create({
                paymentAccount: paymentSettingsComponent.settings.paymentAccount.clone(),
                paymentMethods: [...paymentSettingsComponent.settings.paymentMethods],
                allowLegalEntityInvoices: paymentSettingsComponent.settings.allowLegalEntityInvoices,
                allowOfflineOrderReceipts: paymentSettingsComponent.settings.allowOfflineOrderReceipts,
              }),
              products: null,
              donations: null,
            }),
        );

        components.addOrReplaceComponent(paymentProtocolComponent);
      }

      components.removeComponentByTypeId(LegacyPaymentSettingsComponent.TYPE_ID);
      map['components'] = components.serialize(context);

      version = 9;
    }

    /**
     * Version 10:
     * - add {@link LocationComponent} copied from legacy {@link PlaceComponent} when present.
     */
    if(version < 10) {
      const components = ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
      );

      const locationComponent = components.findComponentByTypeId<LocationComponent>(LocationComponent.TYPE_ID);
      const placeComponent = components.findComponentByTypeId<PlaceComponent>(PlaceComponent.TYPE_ID);
      if(locationComponent == null && placeComponent != null) {
        components.addOrReplaceComponent(LocationComponent.fromPlace(placeComponent.place));
        map['components'] = components.serialize(context);
      }

      version = 10;
    }

    /**
     * Version 11:
     * - In this version TicketAsPass booking protocol was changed.
     */
    if(version < 11) {
      const bookingProtocol = context.fromData(MapUtils.getRecord(map, 'bookingProtocol'));
      if(TicketAsPassEventBookingProtocol.assertType(bookingProtocol)) {
        const components = ComponentList.deserialize(
            context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
        );

        const ticketComponent = components.findComponentByTypeId<TicketTemplateComponent>(TicketTemplateComponent.TYPE_ID);
        if(ticketComponent != null) {
          const passTemplateIds = this.findPassTemplateIdsForTicketAsPassProtocol(ticketComponent.tickets);
          map['bookingProtocol'] = context.toData(TicketAsPassEventBookingProtocol.legacy({
            passTemplateIds,
            shouldSendElectronicPass: bookingProtocol.shouldSendElectronicPass,
          }));
        }
      }

      version = 11;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(context: SerializationContext, model: EventProfile, map: SerializedData): SerializedData {
    let version = model.$schemaVersion.to;

    if(version >= 11) {
      const locationComponent = model.findComponentByTypeId<LocationComponent>(LocationComponent.TYPE_ID);
      const placeComponent = model.findComponentByTypeId<PlaceComponent>(PlaceComponent.TYPE_ID);

      if(locationComponent != null && placeComponent == null) {
        const components = model.components.clone();
        components.addOrReplaceComponent(new PlaceComponent(
            Place.fromMap(context, locationComponent.toMap(context))
        ));
        map['components'] = components.serialize(context);
      }

      version = 9;
      map['$schema_version'] = VersionRange.encodedBetween(9, 11);
    }
    return map;
  }

  private findPassTemplateIdsForTicketAsPassProtocol(tickets: TicketTemplate[]): string[] {
    const passTemplateIds: string[] = [];

    for(const ticket of tickets) {
      const component = ticket.findComponentByTypeId<OpenAccessPassComponent>(OpenAccessPassComponent.TYPE_ID);
      const ticketPassTemplateId = component?.getTemplateId() ?? null;

      if(ticketPassTemplateId != null) {
        passTemplateIds.push(ticketPassTemplateId);
      }
    }

    return passTemplateIds;
  }
}
